import { useQuery } from '@tanstack/react-query';
import GraphqlService from '../service/graphqlService';
import { DEFENDER_ENABLED_PROJECT_CODES } from '../config/deviceDefender';

/**
 * Fetches device quarantine status from Device Defender
 * @param {number} projectCode - Project code for multi-tenant isolation
 * @param {string} deviceSerial - Device serial number
 * @returns {Object} TanStack Query result with quarantine status
 * @property {Object} data - Response data with __typename for state handling
 * @property {boolean} isLoading - True while initial fetch is in progress
 * @property {boolean} isFetching - True during any fetch (initial or refetch)
 * @property {Error} error - Error object if query failed
 */
const useDeviceQuarantineStatus = (projectCode, deviceSerial) => {
  // Only fetch for enabled projects to avoid unnecessary API calls
  const isDefenderEnabled = DEFENDER_ENABLED_PROJECT_CODES.includes(Number(projectCode));

  const getDeviceQuarantineStatus = async () => GraphqlService.getDeviceQuarantineStatus({
    projectId: projectCode,
    deviceSerial,
  });

  const { data, isLoading, isFetching, error } = useQuery(
    ['deviceQuarantineStatus', projectCode, deviceSerial],
    getDeviceQuarantineStatus,
    {
      enabled: !!projectCode && !!deviceSerial && isDefenderEnabled,
      staleTime: 30000, // 30 seconds - balance between freshness and API calls
      retry: 1, // Don't retry excessively for status checks
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  );

  return {
    data,
    isLoading,
    isFetching,
    error,
    isDefenderEnabled,
  };
};

export default useDeviceQuarantineStatus;
